import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchMtrList } from '#/api/imsMgmt/settings/mtr/fetchList';
import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { MTR_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { buildMtrPickerColumns } from '#/modules/imsMgmt/settings/mtr/data/mtrAssemblyColumns';
import { mapMtrRowToAssemblyOption } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import useMtrPickerListQuery from '#/modules/imsMgmt/settings/mtr/hooks/useMtrPickerListQuery';
import { fromTree } from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import {
  mtrListQueryKey,
  mtrStructTreeQueryKey,
} from '#/utils/queryKeys';

function buildInitialSelection(initialSelectedRows = []) {
  const rowSelection = {};
  const selectedById = {};

  initialSelectedRows.forEach((row) => {
    const id = row?.optionMtrId;
    if (!id) {
      return;
    }

    rowSelection[id] = true;
    selectedById[id] = {
      optionMtrId: id,
      mtrNo: row.mtrNo ?? '',
      name: row.name ?? '',
      status: row.status ?? '',
      baseUnitPrice: row.baseUnitPrice ?? null,
    };
  });

  return { rowSelection, selectedById };
}

const MtrPickerDialog = ({
  open,
  onOpenChange,
  excludeMtrId = null,
  initialSelectedRows = [],
  onConfirm,
  title = '請選擇一般物料',
  description = '選擇要當成替代料選項的一般物料。',
  extraFilters = {},
  includeMtrKind = true,
  enableMultiSelection = true,
}) => {
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useMtrPickerListQuery({ extraFilters, includeMtrKind });

  const normalizedInitialRows = useMemo(() => {
    if (enableMultiSelection) {
      return initialSelectedRows;
    }
    return initialSelectedRows.slice(0, 1);
  }, [enableMultiSelection, initialSelectedRows]);

  const initialSelection = useMemo(
    () => buildInitialSelection(normalizedInitialRows),
    [normalizedInitialRows],
  );

  const [selectedById, setSelectedById] = useState(initialSelection.selectedById);
  const [searchKeyword, setSearchKeyword] = useState('');

  const listQueryResult = useQuery({
    queryKey: mtrListQueryKey(listQuery),
    queryFn: () => fetchMtrList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
    enabled: open,
  });

  const structModel = useMemo(
    () => fromTree(treeQuery.data?.data ?? []),
    [treeQuery.data?.data],
  );

  const rows = useMemo(() => {
    const data = listQueryResult.data?.data ?? [];
    if (!excludeMtrId) {
      return data;
    }

    return data.filter((row) => row.id !== excludeMtrId);
  }, [listQueryResult.data?.data, excludeMtrId]);

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(() => ([
    createSelectColumn({
      enableMultiSelection,
      showHeaderCheckbox: enableMultiSelection,
    }),
    ...buildMtrPickerColumns({
      filters: listQuery.filters,
      onFilterChange: handleFilterChange,
      includeMtrKindColumn: !includeMtrKind,
    }),
  ]), [listQuery.filters, handleFilterChange, includeMtrKind, enableMultiSelection]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { structModel }),
    [columns, structModel],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      if (!enableMultiSelection) {
        const selectedId = Object.keys(selection).find((key) => selection[key]);
        if (!selectedId) {
          return {};
        }
        const row = rows.find((item) => String(item.id) === String(selectedId));
        if (row) {
          return { [selectedId]: mapMtrRowToAssemblyOption(row) };
        }
        return prev[selectedId] ? { [selectedId]: prev[selectedId] } : {};
      }

      const next = { ...prev };

      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id]) {
          next[id] = mapMtrRowToAssemblyOption(row);
        } else {
          delete next[id];
        }
      });

      return next;
    });
  }, [rows, enableMultiSelection]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    onConfirm(Object.values(selectedById).filter(Boolean));
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋料號、名稱、備註或原廠料號"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className={cn(
              'hover:text-gray-50',
              'size-10 shrink-0',
            )}
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={false}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選料"
      confirmDisabled={listQueryResult.isLoading}
    >
      <ListTablePanel
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? {
          current_page: 1,
          page_size: 10,
          total_count: 0,
          total_page: 1,
        }}
        isLoading={listQueryResult.isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={MTR_LIST_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection={enableMultiSelection}
        paginationClassName="px-0 py-2"
        initialRowSelection={initialSelection.rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

MtrPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  excludeMtrId: PropTypes.string,
  initialSelectedRows: PropTypes.arrayOf(PropTypes.shape({
    optionMtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  })),
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  extraFilters: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  includeMtrKind: PropTypes.bool,
  enableMultiSelection: PropTypes.bool,
};

export default MtrPickerDialog;
